import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useCurrentUserQuery } from '@dashboard/generated/wp/authentication';
import { Box } from '@mui/material';
import CodeMirrorPreview from '@wp-react/components/CodeMirror/CodeMirrorPreview';

import { useCurrentWaypointTemplateQuery } from '../../../../useInitialValues';

type TType = 'NODE' | 'CURL';
type Props = {
  type: TType;
  onCodeGenerated?: (code: string) => void;
};

type ApiProps = {
  url: string;
  data: Record<string, unknown>;
};

function getCodeExample(type: TType, { url, data }: ApiProps): string {
  switch (type) {
    case 'CURL':
      return `curl "${url}" \\
  -H 'Content-Type: application/json' \\
  -u 'USERNAME:PASSWORD' \\
  -d '${JSON.stringify(data, null, '  ')}'`;
    case 'NODE': {
      const payload = {
        headers: { 'Content-Type': 'application/json' },
        method: 'post',
        url,
        auth: { username: 'USERNAME', password: 'PASSWORD' },
        data,
      };
      return `const axios = require('axios');

axios(${JSON.stringify(payload, null, '  ')})
.then(function (response) {
  console.log(response);
})
.catch(function (error) {
  console.log(error);
})`;
    }
  }
}

const DEFAULT_VARIABLES = {};

export default function CodeExampleContent({ type, onCodeGenerated }: Props) {
  const { id } = useParams<{ id: string }>();
  const { data: userData } = useCurrentUserQuery();
  const { data } = useCurrentWaypointTemplateQuery();
  const emailAddress = userData?.currentUser?.emailAddress ?? '';
  const variables = data?.WaypointTemplate.defaultPreviewVariables ?? DEFAULT_VARIABLES;
  const codeExample = useMemo(() => {
    const data: Record<string, unknown> = {
      templateId: id,
      to: emailAddress,
      variables,
    };
    return getCodeExample(type, { url: `https://live.waypointapi.com/v1/email_messages`, data });
  }, [id, emailAddress, type, variables]);

  useEffect(() => {
    if (onCodeGenerated) {
      onCodeGenerated(codeExample);
    }
  }, [codeExample, onCodeGenerated]);

  return (
    <Box sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
      <CodeMirrorPreview key={codeExample} language="JSON" value={codeExample} />
    </Box>
  );
}
